import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Layout, Layers } from 'lucide-react';
import { useSurveyStore } from '../store/surveyStore';
import QuestionRenderer from './QuestionRenderer';
import { sendSurveyNotification, sendAISurveyNotification } from '../services/emailService';
import type { DisplayMode } from '../types/survey';

export default function SurveyForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { 
    currentSurvey, 
    questions, 
    choices,
    loading, 
    error, 
    fetchSurvey, 
    responses, 
    viewMode, 
    setViewMode,
    clearResponses
  } = useSurveyStore();
  
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Clear responses and fetch survey when component mounts or ID changes
  useEffect(() => {
    if (id) {
      // Clear any previous responses
      clearResponses();
      // Then fetch the survey
      fetchSurvey(id);
    }
    
    // Reset current slide to first question
    setCurrentSlide(0);
    
    // Reset any submission errors
    setSubmitError(null);
  }, [id, fetchSurvey, clearResponses]);

  const handleSubmit = async () => {
    if (!id || !currentSurvey || isSubmitting) return;

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      // Find the contact form question
      const contactFormQuestion = questions.find(q => q.question_type === 'contact_form');
      if (!contactFormQuestion) {
        setSubmitError('Contact form not found');
        return;
      }

      // Get the contact form response
      const contactResponse = responses.find(r => r.question_id === contactFormQuestion.id);
      if (!contactResponse?.text_response) {
        setSubmitError('Please fill out the contact form');
        return;
      }

      // Parse the contact form data
      const contactData = JSON.parse(contactResponse.text_response);
      
      // Check required fields
      if (!contactData.name || !contactData.name.trim()) {
        setSubmitError('Name is required');
        return;
      }

      if (!contactData.email || !contactData.email.trim()) {
        setSubmitError('Email is required');
        return;
      }

      // Website is now required
      if (!contactData.website || !contactData.website.trim()) {
        setSubmitError('Website is required');
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(contactData.email)) {
        setSubmitError('Please enter a valid email address');
        return;
      }

      // Check if this is the AI survey
      const isAISurvey = currentSurvey.title.includes('AI Audit') || 
                         currentSurvey.description.includes('AI Audit');

      let emailSent = false;
      
      if (isAISurvey) {
        // Process AI survey responses
        const aiSurveyData = {
          name: contactData.name,
          email: contactData.email,
          website: contactData.website,
          phone: contactData.phone
        };

        // Find and process other question responses
        questions.forEach(question => {
          const response = responses.find(r => r.question_id === question.id);
          if (!response) return;

          // Skip the contact form question as we already processed it
          if (question.id === contactFormQuestion.id) return;

          // For multiple choice questions, get the choice text instead of just the ID
          if (question.question_type === 'multiple_choice' && response.choice_id) {
            const selectedChoice = choices.find(c => c.id === response.choice_id);
            
            // Process based on question text
            if (question.question_text.includes('type of business')) {
              aiSurveyData.businessType = selectedChoice ? [selectedChoice.choice_text] : undefined;
            } else if (question.question_text.includes('big is your team')) {
              aiSurveyData.teamSize = selectedChoice ? selectedChoice.choice_text : undefined;
            } else if (question.question_text.includes('spend the most time')) {
              aiSurveyData.timeSpenders = selectedChoice ? [selectedChoice.choice_text] : undefined;
            } else if (question.question_text.includes('using any AI tools')) {
              aiSurveyData.aiToolsUsed = selectedChoice ? [selectedChoice.choice_text] : undefined;
            } else if (question.question_text.includes('comfort level with AI')) {
              aiSurveyData.aiComfortLevel = selectedChoice ? selectedChoice.choice_text : undefined;
            } else if (question.question_text.includes('#1 goal with AI')) {
              aiSurveyData.aiGoal = selectedChoice ? selectedChoice.choice_text : undefined;
            }
          } else {
            // Process text responses
            if (question.question_text.includes('type of business')) {
              aiSurveyData.businessType = response.text_response ? 
                JSON.parse(response.text_response) : undefined;
            } else if (question.question_text.includes('big is your team')) {
              aiSurveyData.teamSize = response.text_response;
            } else if (question.question_text.includes('spend the most time')) {
              aiSurveyData.timeSpenders = response.text_response ? 
                JSON.parse(response.text_response) : undefined;
            } else if (question.question_text.includes('manual, repetitive, or frustrating')) {
              aiSurveyData.painPoints = response.text_response;
            } else if (question.question_text.includes('using any AI tools')) {
              aiSurveyData.aiToolsUsed = response.text_response ? 
                JSON.parse(response.text_response) : undefined;
            } else if (question.question_text.includes('tools or systems')) {
              aiSurveyData.dailyTools = response.text_response;
            } else if (question.question_text.includes('comfort level with AI')) {
              aiSurveyData.aiComfortLevel = response.text_response;
            } else if (question.question_text.includes('#1 goal with AI')) {
              aiSurveyData.aiGoal = response.text_response;
            } else if (question.question_text.includes('Anything else')) {
              aiSurveyData.additionalInfo = response.text_response;
            }
          }
        });

        console.log('Sending AI survey data:', aiSurveyData);

        // Send AI survey notification
        emailSent = await sendAISurveyNotification(aiSurveyData);
      } else {
        // Send regular survey notification
        emailSent = await sendSurveyNotification(contactData);
      }
      
      if (!emailSent) {
        console.warn('Failed to send email notification, but continuing with submission');
      }

      // Clear responses before navigating away
      clearResponses();

      // Use React Router's navigate with replace to prevent back navigation
      navigate('/thank-you', { replace: true });
    } catch (error) {
      console.error('Error submitting survey:', error);
      setSubmitError(error instanceof Error ? error.message : 'An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F98B3D]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-md">
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  if (!currentSurvey) {
    return (
      <div className="bg-yellow-50 p-4 rounded-md">
        <p className="text-yellow-700">Survey not found</p>
      </div>
    );
  }

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => Math.max(0, prev - 1));
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) => Math.min(questions.length - 1, prev + 1));
  };

  const handleViewModeChange = (mode: DisplayMode) => {
    setViewMode(mode);
  };

  // Override the is_required flag for questions
  const modifiedQuestions = questions.map(question => ({
    ...question,
    is_required: question.question_type === 'contact_form'
  }));

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{currentSurvey.title}</h1>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleViewModeChange('form')}
            className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium ${
              viewMode === 'form'
                ? 'bg-[#F98B3D] text-white border-[#F98B3D]'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Layers className="h-4 w-4 mr-2" />
            Form View
          </button>
          <button
            onClick={() => handleViewModeChange('slide')}
            className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium ${
              viewMode === 'slide'
                ? 'bg-[#F98B3D] text-white border-[#F98B3D]'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Layout className="h-4 w-4 mr-2" />
            Slide View
          </button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        {submitError && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
            <p className="text-red-700">{submitError}</p>
          </div>
        )}
        
        {viewMode === 'form' ? (
          <div className="px-4 py-5 sm:p-6">
            <p className="text-gray-600 mb-8">{currentSurvey.description}</p>
            <div className="space-y-8">
              {modifiedQuestions.map((question) => (
                <QuestionRenderer key={question.id} question={question} />
              ))}
            </div>
            <div className="mt-8 flex justify-end">
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-[#F98B3D] hover:bg-[#e07a2c] transition-colors duration-200 ${
                  isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Survey'}
              </button>
            </div>
          </div>
        ) : (
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-medium text-gray-500">
                Question {currentSlide + 1} of {modifiedQuestions.length}
              </p>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handlePrevSlide}
                  disabled={currentSlide === 0}
                  className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium ${
                    currentSlide === 0
                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </button>
                <button
                  onClick={handleNextSlide}
                  disabled={currentSlide === modifiedQuestions.length - 1}
                  className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium ${
                    currentSlide === modifiedQuestions.length - 1
                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              </div>
            </div>
            <div className="min-h-[400px] flex flex-col">
              {modifiedQuestions[currentSlide] && (
                <QuestionRenderer question={modifiedQuestions[currentSlide]} />
              )}
            </div>
            <div className="mt-4 w-full bg-gray-200 rounded-full h-1">
              <div
                className="bg-[#F98B3D] h-1 rounded-full transition-all duration-300"
                style={{
                  width: `${((currentSlide + 1) / modifiedQuestions.length) * 100}%`,
                }}
              ></div>
            </div>
            {currentSlide === modifiedQuestions.length - 1 && (
              <div className="mt-8 flex justify-end">
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-[#F98B3D] hover:bg-[#e07a2c] transition-colors duration-200 ${
                    isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Survey'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}